import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageLoader } from '@/components/ui/loading-spinner'
import { Toaster } from '@/components/ui/toaster'
import { StructuredData } from '@/components/StructuredData'

// Public pages - Eager loaded
import { RootPage } from '@/routes/RootPage'
import { PrivacyPolicy } from '@/routes/legal/PrivacyPolicy'
import { TermsOfService } from '@/routes/legal/TermsOfService'
import { About } from '@/routes/legal/About'

// Auth pages - Eager loaded (small, needed immediately)
import { LoginPage } from '@/routes/auth/LoginPage'
import { RegisterPage } from '@/routes/auth/RegisterPage'
import { VerifyEmailPage } from '@/routes/auth/VerifyEmailPage'

// Main pages - Lazy loaded for code splitting
const DashboardPage = lazy(() => import('@/routes/DashboardPage'))
const AircraftListPage = lazy(() => import('@/routes/aircraft/AircraftListPage'))
const AircraftDetailPage = lazy(() => import('@/routes/aircraft/AircraftDetailPage'))
const AircraftCreatePage = lazy(() => import('@/routes/aircraft/AircraftCreatePage'))
const AircraftEditPage = lazy(() => import('@/routes/aircraft/AircraftEditPage'))
const JournalsListPage = lazy(() => import('@/routes/journals/JournalsListPage'))
const JournalDetailPage = lazy(() => import('@/routes/journals/JournalDetailPage'))
const JournalCreatePage = lazy(() => import('@/routes/journals/JournalCreatePage'))
const JournalEditPage = lazy(() => import('@/routes/journals/JournalEditPage'))
const ReportsPage = lazy(() => import('@/routes/ReportsPage'))
const PilotLogbookPage = lazy(() => import('@/routes/reports/PilotLogbookPage'))
const InvitationsPage = lazy(() => import('@/routes/invitations/InvitationsPage'))
const DiagnosticPage = lazy(() => import('@/routes/DiagnosticPage'))
const ProfilePage = lazy(() => import('@/routes/profile/ProfilePage'))

function App() {
  const { init } = useAuthStore()

  // Initialize auth on mount
  useEffect(() => {
    init()
  }, [init])

  return (
    <ErrorBoundary>
      <StructuredData />
      <Toaster />
      <Routes>
        {/* Root route - landing page for unauthenticated, dashboard for authenticated */}
        <Route path="/" element={<RootPage />} />

        {/* Public legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/about" element={<About />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Protected routes with lazy loading */}
        <Route element={<AppLayout />}>
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<PageLoader />}>
                <DashboardPage />
              </Suspense>
            }
          />

          {/* Profile */}
          <Route
            path="/profile"
            element={
              <Suspense fallback={<PageLoader />}>
                <ProfilePage />
              </Suspense>
            }
          />

          {/* Aircraft routes */}
          <Route
            path="/aircraft"
            element={
              <Suspense fallback={<PageLoader />}>
                <AircraftListPage />
              </Suspense>
            }
          />
          <Route
            path="/aircraft/create"
            element={
              <Suspense fallback={<PageLoader />}>
                <AircraftCreatePage />
              </Suspense>
            }
          />
          <Route
            path="/aircraft/:id"
            element={
              <Suspense fallback={<PageLoader />}>
                <AircraftDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/aircraft/:id/edit"
            element={
              <Suspense fallback={<PageLoader />}>
                <AircraftEditPage />
              </Suspense>
            }
          />

          {/* Journal routes */}
          <Route
            path="/journals"
            element={
              <Suspense fallback={<PageLoader />}>
                <JournalsListPage />
              </Suspense>
            }
          />
          <Route
            path="/journals/create"
            element={
              <Suspense fallback={<PageLoader />}>
                <JournalCreatePage />
              </Suspense>
            }
          />
          <Route
            path="/journals/:id"
            element={
              <Suspense fallback={<PageLoader />}>
                <JournalDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/journals/:id/edit"
            element={
              <Suspense fallback={<PageLoader />}>
                <JournalEditPage />
              </Suspense>
            }
          />

          {/* Invitations */}
          <Route
            path="/invitations"
            element={
              <Suspense fallback={<PageLoader />}>
                <InvitationsPage />
              </Suspense>
            }
          />

          {/* Reports */}
          <Route
            path="/reports"
            element={
              <Suspense fallback={<PageLoader />}>
                <ReportsPage />
              </Suspense>
            }
          />
          <Route
            path="/reports/pilot-logbook"
            element={
              <Suspense fallback={<PageLoader />}>
                <PilotLogbookPage />
              </Suspense>
            }
          />

          {/* Diagnostic */}
          <Route
            path="/diagnostic"
            element={
              <Suspense fallback={<PageLoader />}>
                <DiagnosticPage />
              </Suspense>
            }
          />
        </Route>

        {/* Catch all - redirect to root which handles auth logic */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App
